import React, { useCallback, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import { useTranslation } from '~/dependencies';
import { ExternalUrlIcon } from '~/assets/svgIcons';
import { colors } from '~/layout';
import { electron } from '~/helpers';
import ConfirmationBox from '../../../components/ConfirmationBox';

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;
const SKIP_EXTERNAL_LINK_WARNING_KEY = 'skipExternalLinkWarning';

type Segment = { kind: 'text'; value: string } | { kind: 'url'; value: string };

function linkifyText(text: string): Segment[] {
  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text)) !== null) {
    const url = match[0];
    const start = match.index;

    if (start > lastIndex) {
      segments.push({ kind: 'text', value: text.slice(lastIndex, start) });
    }
    segments.push({ kind: 'url', value: url });

    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    segments.push({ kind: 'text', value: text.slice(lastIndex) });
  }

  return segments;
}

function highlight(value: string, searchValue: string) {
  return (
    <Highlighter
      highlightClassName="highlighter"
      searchWords={[searchValue]}
      autoEscape
      textToHighlight={value}
    />
  );
}

export default function MessageText({
  text,
  searchValue,
}: {
  text: string;
  searchValue: string;
}) {
  const { t } = useTranslation();
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const segments = linkifyText(text);
  const hasLinks = segments.some(s => s.kind === 'url');

  const openExternalUrl = useCallback((url: string) => {
    if (localStorage.getItem(SKIP_EXTERNAL_LINK_WARNING_KEY) === 'true') {
      electron.openExternalUrl({ url });
      return;
    }
    setDontAskAgain(false);
    setPendingUrl(url);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setPendingUrl(null);
  }, []);

  const handleConfirmOpenUrl = useCallback(() => {
    if (pendingUrl) {
      if (dontAskAgain) {
        localStorage.setItem(SKIP_EXTERNAL_LINK_WARNING_KEY, 'true');
      }
      electron.openExternalUrl({ url: pendingUrl });
    }
    setPendingUrl(null);
  }, [pendingUrl, dontAskAgain]);

  if (!hasLinks) {
    return highlight(text, searchValue);
  }

  return (
    <>
      <ConfirmationBox
        open={pendingUrl !== null}
        close={handleCloseDialog}
        title={t('screens.conversation.externalLinkWarning')}
        icon={ExternalUrlIcon}
        isLoading={false}
        btnColor={colors.lightBlue13}
        btnName={t('screens.conversation.btnOpenLink')}
        width={320}
        handleAction={handleConfirmOpenUrl}
        extra={
          <FormControlLabel
            style={{ marginTop: 10, marginLeft: 0, marginRight: 0, gap: 0 }}
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={dontAskAgain}
                onChange={e => setDontAskAgain(e.target.checked)}
                style={{ padding: 0 }}
              />
            }
            label={
              <span
                style={{
                  marginLeft: '-20px',
                  fontSize: '0.8rem',
                  color: colors.darkGray12,
                }}>
                {t('screens.conversation.dontAskAgain')}
              </span>
            }
          />
        }
      />
      {segments.map((segment, idx) => {
        if (segment.kind === 'text') {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`t-${idx}`}>
              {highlight(segment.value, searchValue)}
            </React.Fragment>
          );
        }
        return (
          <a
            // eslint-disable-next-line react/no-array-index-key
            key={`u-${idx}`}
            href={segment.value}
            onClick={e => {
              e.preventDefault();
              openExternalUrl(segment.value);
            }}>
            {highlight(segment.value, searchValue)}
          </a>
        );
      })}
    </>
  );
}
